/* Khurjeen theme — shared behaviours */

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  };
}

function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (format || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, withCommas);
}

/* Quantity input --------------------------------------------------------- */
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    if (event.currentTarget.name === 'plus') this.input.stepUp();
    else this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('quantity-input', QuantityInput);

/* Announcement bar / generic slideshow ----------------------------------- */
class SlideshowComponent extends HTMLElement {
  constructor() {
    super();
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    if (this.slides.length === 0) return;
    this.index = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;

    this.querySelectorAll('[data-slider-prev]').forEach((b) =>
      b.addEventListener('click', () => this.jump(this.index - 1))
    );
    this.querySelectorAll('[data-slider-next]').forEach((b) =>
      b.addEventListener('click', () => this.jump(this.index + 1))
    );

    this.dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.jump(i)));

    this.show(0);
    if (this.autoplay && this.slides.length > 1) this.start();

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => { if (this.autoplay) this.start(); });
  }

  disconnectedCallback() { this.stop(); }

  start() { this.stop(); this.timer = setInterval(() => this.move(1), this.speed); }
  stop() { if (this.timer) clearInterval(this.timer); }

  move(step) { this.show((this.index + step + this.slides.length) % this.slides.length); }

  /* a manual move restarts the timer, so a slide never flips away
     immediately after the visitor clicked to reach it */
  jump(index) {
    this.show((index + this.slides.length) % this.slides.length);
    if (this.autoplay && this.slides.length > 1) this.start();
  }

  show(index) {
    this.index = index;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    if (!this.dots) return;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}
customElements.define('slideshow-component', SlideshowComponent);

/* Mobile menu ------------------------------------------------------------ */
class MobileMenu extends HTMLElement {
  constructor() {
    super();
    this.overlay = document.querySelector('.mobilemenu__overlay');
    document.querySelectorAll('[data-mobilemenu="toggle"]').forEach((button) =>
      button.addEventListener('click', () => this.toggle(button))
    );
    this.querySelectorAll('[data-mobilemenu="close"]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    document.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
  }

  toggle(button) {
    const open = this.classList.toggle('is-open');
    document.body.classList.toggle('mobilemenu-open', open);
    button.setAttribute('aria-expanded', open);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('mobilemenu-open');
    document
      .querySelectorAll('[data-mobilemenu="toggle"]')
      .forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }
}
customElements.define('mobilemenu-nav', MobileMenu);

/* Product media gallery -------------------------------------------------- */
class MediaGallery extends HTMLElement {
  constructor() {
    super();
    this.viewer = this.querySelector('[data-gallery-viewer]');
    this.thumbnails = this.querySelectorAll('[data-gallery-thumb]');
    this.thumbnails.forEach((thumb) =>
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        this.setActiveMedia(thumb.dataset.mediaId);
      })
    );
  }

  setActiveMedia(mediaId) {
    const target = this.querySelector(`[data-media-id="${mediaId}"]`);
    if (!target || !this.viewer) return;
    this.viewer.scrollTo({ left: target.offsetLeft - this.viewer.offsetLeft, behavior: 'smooth' });
    this.thumbnails.forEach((t) => t.classList.toggle('is-active', t.dataset.mediaId === mediaId));
  }
}
customElements.define('media-gallery', MediaGallery);

/* Variant selection ------------------------------------------------------ */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  get variantData() {
    if (!this._variantData) {
      const script = this.querySelector('[type="application/json"]');
      this._variantData = script ? JSON.parse(script.textContent) : [];
    }
    return this._variantData;
  }

  onVariantChange() {
    const options = Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map(
      (input) => input.value
    );
    const variant = this.variantData.find((v) =>
      v.options.every((option, index) => option === options[index])
    );
    this.updateOptionLabels();
    if (!variant) {
      this.setUnavailable();
      return;
    }
    this.updateURL(variant);
    this.updateFormId(variant);
    this.updatePrice(variant);
    this.updateButton(!variant.available);
    /* Let other product-page blocks (bundle builder, etc.) follow along */
    document.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: { variant, sectionId: this.dataset.section }
      })
    );
  }

  updateOptionLabels() {
    this.querySelectorAll('[data-selected-value]').forEach((label) => {
      const index = label.dataset.optionIndex;
      const checked = this.querySelector(`input[name="option${index}"]:checked`);
      if (checked) label.textContent = checked.value;
    });
  }

  updateURL(variant) {
    if (!variant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
  }

  updateFormId(variant) {
    document
      .querySelectorAll(`form[data-product-form="${this.dataset.section}"] input[name="id"]`)
      .forEach((input) => {
        input.value = variant.id;
        input.disabled = false;
      });
  }

  updatePrice(variant) {
    const container = document.getElementById(`price-${this.dataset.section}`);
    if (!container) return;
    fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`)
      .then((r) => r.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const source = html.getElementById(`price-${this.dataset.section}`);
        if (source) container.innerHTML = source.innerHTML;
      })
      .catch(() => {});
  }

  updateButton(disable) {
    document
      .querySelectorAll(`form[data-product-form="${this.dataset.section}"] [name="add"]`)
      .forEach((button) => {
        const text = button.querySelector('span');
        button.disabled = disable;
        if (text) text.textContent = disable ? window.variantStrings.soldOut : window.variantStrings.addToCart;
      });
  }

  setUnavailable() {
    document
      .querySelectorAll(`form[data-product-form="${this.dataset.section}"] [name="add"]`)
      .forEach((button) => {
        const text = button.querySelector('span');
        button.disabled = true;
        if (text) text.textContent = window.variantStrings.unavailable;
      });
  }
}
customElements.define('variant-selects', VariantSelects);

/* Sticky header ---------------------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('.header-wrapper--sticky');
  if (!header) return;
  const setOffset = () =>
    document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  setOffset();
  window.addEventListener('resize', debounce(setOffset, 150));
});
